import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiPackage, FiFileText, FiSave, FiSearch, FiDollarSign } = FiIcons;

const ProductManagement = () => {
  const { savedProducts, setSavedProducts } = useInvoice();
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    currency: 'USD'
  });

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' }
  ];

  // Filter products based on search term
  const filteredProducts = savedProducts.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      currency: product.currency || 'USD'
    });
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      currency: 'USD'
    });
    setIsAddingNew(true);
  };

  const handleSave = () => {
    if (!formData.name) {
      alert('Product name is required');
      return;
    }

    if (isAddingNew) {
      const newProduct = {
        ...formData,
        id: Date.now()
      };
      setSavedProducts(prev => [...prev, newProduct]);
    } else if (editingProduct) {
      setSavedProducts(prev =>
        prev.map(prod =>
          prod.id === editingProduct.id
            ? { ...prod, ...formData }
            : prod
        )
      );
    }

    setEditingProduct(null);
    setIsAddingNew(false);
    setFormData({
      name: '',
      description: '',
      currency: 'USD'
    });
  };

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setSavedProducts(prev => prev.filter(prod => prod.id !== id));
    }
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setIsAddingNew(false);
    setFormData({
      name: '',
      description: '',
      currency: 'USD'
    });
  };

  const getCurrencySymbol = (code) => {
    return currencies.find(c => c.code === code)?.symbol || '$';
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Product & Service Management</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative flex-grow">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <motion.button
            onClick={handleAddNew}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
            Add New Product
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Saved Products ({filteredProducts.length})</h2>
          </div>
          <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
            <AnimatePresence>
              {filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <SafeIcon icon={FiPackage} className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No products found</p>
                  <p className="text-sm">
                    {searchTerm ? 'Try adjusting your search criteria' : 'Add your first product to get started'}
                  </p>
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      editingProduct?.id === product.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="font-medium text-gray-900">{product.name}</h3>
                          {product.currency && (
                            <span className="ml-3 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              {getCurrencySymbol(product.currency)} {product.currency}
                            </span>
                          )}
                        </div>
                        {product.description && (
                          <div className="mt-1 text-sm text-gray-600 line-clamp-2">
                            {product.description}
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <motion.button
                          onClick={() => handleEdit(product)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Edit product"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Delete product"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Edit/Add Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isAddingNew ? 'Add New Product' : editingProduct ? 'Edit Product' : 'Product Details'}
            </h2>
          </div>
          <div className="p-4">
            {(isAddingNew || editingProduct) ? (
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product/Service Name *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiPackage} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Product or service name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiFileText} className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter product or service description"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Currency
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    This is the default currency for this product/service. You can still change the price and currency when creating an invoice.
                  </p>
                </div>

                <div className="flex space-x-3 pt-4">
                  <motion.button
                    onClick={handleSave}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    {isAddingNew ? 'Add Product' : 'Save Changes'}
                  </motion.button>
                  <motion.button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    Cancel
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <SafeIcon icon={FiPackage} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Select a product to edit or add a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductManagement;